import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BarChart3, Link as LinkIcon, ExternalLink, Copy, Check, Users,
  MousePointerClick, TrendingUp, Trash2, ArrowUpRight,
} from "lucide-react";

const SOURCE_COLORS = {
  share_arc: "#10B981",
  sms: "#0EA5E9",
  whatsapp: "#22C55E",
  twitter: "#1DA1F2",
  x: "#1DA1F2",
  email: "#6366F1",
  direct: "#94A3B8",
};

function sourceColor(src) {
  if (!src) return "#94A3B8";
  const key = src.toLowerCase();
  if (SOURCE_COLORS[key]) return SOURCE_COLORS[key];
  // deterministic fallback hue
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 360;
  return `hsl(${h}, 65%, 50%)`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Analytics() {
  const [links, setLinks] = useState(null);
  const [activeCode, setActiveCode] = useState(null);
  const [detail, setDetail] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/shortlinks");
      setLinks(data);
      setActiveCode((cur) => cur || (data.length ? data[0].code : null));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!activeCode) { setDetail(null); return; }
    let alive = true;
    api.get(`/shortlinks/${activeCode}/clicks`).then((r) => { if (alive) setDetail(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [activeCode]);

  const total = (links || []).reduce((a, l) => a + (l.clicks || 0), 0);
  const activeLinkObj = (links || []).find((l) => l.code === activeCode);

  const copy = async (url, code) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1800);
    } catch { /* ignore */ }
  };

  const remove = async (code) => {
    if (!window.confirm("Delete this tracked link? All click history will be lost.")) return;
    await api.delete(`/shortlinks/${code}`);
    if (activeCode === code) setActiveCode(null);
    refresh();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="analytics-page">
      <div className="mb-8">
        <div className="label-eyebrow">Analytics</div>
        <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight mt-1">Share-link analytics</h1>
        <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
          Every Share Arc PNG and Property Report you send carries a tracked URL. See what's
          getting opened, where it's spreading, and which deals are driving the most attention.
        </p>
      </div>

      {/* Hero stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Stat icon={MousePointerClick} label="Total clicks" value={total} data-testid="stat-total-clicks" />
        <Stat icon={LinkIcon} label="Tracked links" value={(links || []).length} data-testid="stat-link-count" />
        <Stat icon={TrendingUp} label="Active in last 24h"
              value={(links || []).filter((l) => l.last_click_at && (Date.now() - new Date(l.last_click_at).getTime()) < 86400000).length}
              data-testid="stat-active-24h" />
      </div>

      {err && <div className="banner-err mb-6">{err}</div>}

      {links === null ? (
        <div className="card text-center py-16 text-[var(--ink-2)]">Loading…</div>
      ) : links.length === 0 ? (
        <div className="card text-center py-16">
          <BarChart3 size={32} className="text-[var(--ink-3)] mx-auto mb-3" />
          <h3 className="font-display font-bold text-lg">No tracked links yet</h3>
          <p className="text-sm text-[var(--ink-2)] mt-2 max-w-md mx-auto">
            Open a lead's Property Analysis report and click <strong>Share arc</strong> — a tracked
            short URL is created automatically. Share that URL anywhere and clicks show up here.
          </p>
          <Link to="/app/leads" className="btn-money mt-4 inline-flex" data-testid="empty-go-leads">
            Go to leads <ArrowUpRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: link list */}
          <div className="lg:col-span-2 space-y-2" data-testid="analytics-link-list">
            {links.map((l) => {
              const isActive = l.code === activeCode;
              return (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setActiveCode(l.code)}
                  className="card w-full text-left transition hover:shadow-md"
                  style={{
                    background: isActive ? "var(--ink)" : undefined,
                    color: isActive ? "#fff" : undefined,
                    borderColor: isActive ? "var(--ink)" : undefined,
                  }}
                  data-testid={`analytics-link-${l.code}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: isActive ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.06)" }}
                    >
                      <LinkIcon size={15} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={"font-mono text-sm font-semibold truncate " + (isActive ? "" : "")}>
                        /d/{l.code}
                      </div>
                      <div className={"text-xs truncate " + (isActive ? "text-white/55" : "text-[var(--ink-3)]")}>
                        {l.kind === "snapshot" ? "Snapshot" : "Report"} · last {timeAgo(l.last_click_at)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-extrabold text-xl tabular-nums">{l.clicks || 0}</div>
                      <div className={"text-[10px] uppercase tracking-widest " + (isActive ? "text-white/55" : "text-[var(--ink-3)]")}>
                        clicks
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: active detail */}
          <div className="lg:col-span-3" data-testid="analytics-detail">
            {activeLinkObj && detail ? (
              <div className="space-y-5">
                {/* URL row */}
                <div className="card">
                  <div className="label-eyebrow flex items-center gap-1.5 mb-2"><LinkIcon size={11} /> Tracked link</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-sm flex-1 min-w-0 truncate p-2 rounded bg-[rgba(15,23,42,0.04)]" data-testid="detail-url">
                      {activeLinkObj.redirect_url}
                    </code>
                    <button type="button" className="btn-outline" onClick={() => copy(activeLinkObj.redirect_url, activeLinkObj.code)} data-testid="detail-copy">
                      {copiedCode === activeLinkObj.code ? <Check size={13} /> : <Copy size={13} />}
                      {copiedCode === activeLinkObj.code ? "Copied" : "Copy"}
                    </button>
                    <a href={activeLinkObj.target_full_url} target="_blank" rel="noreferrer" className="btn-outline" data-testid="detail-open">
                      <ExternalLink size={13} /> Open
                    </a>
                    <button type="button" className="btn-outline text-[var(--warn)]" onClick={() => remove(activeLinkObj.code)} data-testid="detail-delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <Mini label="Total" value={detail.clicks_total} />
                    <Mini label="Unique" value={detail.unique_visitors} icon={Users} />
                    <Mini label="Last hit" value={timeAgo(detail.last_click_at)} small />
                  </div>
                </div>

                {/* By source */}
                <div className="card">
                  <div className="label-eyebrow mb-3">Top sources</div>
                  {Object.keys(detail.by_source || {}).length === 0 ? (
                    <div className="text-sm text-[var(--ink-2)]">No clicks yet — share the link to start collecting data.</div>
                  ) : (
                    <SourceBars data={detail.by_source} total={detail.clicks_total} />
                  )}
                </div>

                {/* Recent clicks */}
                <div className="card">
                  <div className="label-eyebrow mb-3">Recent clicks</div>
                  {detail.recent.length === 0 ? (
                    <div className="text-sm text-[var(--ink-2)]">No clicks yet.</div>
                  ) : (
                    <ul className="divide-y divide-[var(--line)]" data-testid="detail-recent-list">
                      {detail.recent.slice(0, 30).map((c) => (
                        <li key={c.id} className="py-2.5 flex items-center gap-3 text-sm">
                          <span className="chip" style={{
                            background: sourceColor(c.utm_source) + "22",
                            color: sourceColor(c.utm_source),
                            borderColor: sourceColor(c.utm_source) + "55",
                            fontSize: 11, padding: "2px 8px",
                          }}>
                            <span className="chip-dot" style={{ background: sourceColor(c.utm_source) }} />
                            {c.utm_source || "direct"}
                          </span>
                          <span className="text-[var(--ink-2)] flex-1 truncate text-xs">{c.ua || "—"}</span>
                          {c.returning && (
                            <span className="chip" style={{ fontSize: 10, padding: "2px 6px" }}>↺ returning</span>
                          )}
                          <span className="text-xs text-[var(--ink-3)] shrink-0">{timeAgo(c.ts)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : (
              <div className="card text-center py-16 text-[var(--ink-2)]">
                {activeCode ? "Loading…" : "Select a link to see details"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, ...rest }) {
  return (
    <div className="card" {...rest}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center">
          <Icon size={18} />
        </div>
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display font-extrabold text-2xl tabular-nums mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, icon: Icon, small }) {
  return (
    <div className="rounded-lg p-3" style={{ background: "rgba(15,23,42,0.04)" }}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold flex items-center gap-1">
        {Icon ? <Icon size={10} /> : null} {label}
      </div>
      <div className={"font-display font-bold tabular-nums mt-1 " + (small ? "text-base" : "text-2xl")}>{value}</div>
    </div>
  );
}

function SourceBars({ data, total }) {
  const items = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(...items.map(([, v]) => v));
  return (
    <div className="space-y-2.5" data-testid="source-bars">
      {items.map(([src, count]) => (
        <div key={src} className="flex items-center gap-3">
          <span className="text-xs font-semibold min-w-[80px] text-[var(--ink-2)] truncate">{src}</span>
          <div className="flex-1 h-7 rounded-md overflow-hidden bg-[rgba(15,23,42,0.04)] relative">
            <div
              className="h-full transition-all"
              style={{ width: `${(count / max) * 100}%`, background: sourceColor(src) }}
            />
            <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold tabular-nums">
              {count} · {Math.round((count / (total || 1)) * 100)}%
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
